import { Opportunity, OpportunityPayload } from 'types/opportunity';

import { getOpportunityClientName } from './utils';

export type OpportunityFormData = {
  description: string;
  origin_id: number | null;
  responsible_id: number | null;
  service_type: string;
  opportunity_date: string;
  estimated_value: number | null;
  client_id: number | null;
  client_name: string;
  zip_code: string;
  city_id: number | null;
  city_name: string;
  address: string;
  number: string;
  complement: string;
  district: string;
  observation: string;
};

export const opportunityFormDefaultValues: OpportunityFormData = {
  description: '',
  origin_id: null,
  responsible_id: null,
  service_type: '',
  opportunity_date: '',
  estimated_value: null,
  client_id: null,
  client_name: '',
  zip_code: '',
  city_id: null,
  city_name: '',
  address: '',
  number: '',
  complement: '',
  district: '',
  observation: '',
};

const toNullableText = (value: string | null | undefined): string | null =>
  value?.trim() ? value.trim() : null;

export const formatOpportunityCity = (
  city: Opportunity['city'] | undefined,
): string =>
  city?.name
    ? [city.name, city.state?.abbreviation].filter(Boolean).join('/')
    : '';

export const formatOpportunityAddress = (
  opportunity: Pick<
    Opportunity,
    'address' | 'number' | 'complement' | 'district' | 'city' | 'zip_code'
  >,
): string => {
  const street = [opportunity.address, opportunity.number]
    .filter(Boolean)
    .join(', ');

  return [
    street,
    opportunity.complement,
    opportunity.district,
    formatOpportunityCity(opportunity.city),
    opportunity.zip_code ? `CEP ${opportunity.zip_code}` : '',
  ]
    .filter(Boolean)
    .join(' - ');
};

export const mapOpportunityToFormData = (
  opportunity: Opportunity,
): OpportunityFormData => ({
  description: opportunity.description || '',
  origin_id: opportunity.origin?.id ?? null,
  responsible_id: opportunity.responsible?.id ?? null,
  service_type: opportunity.service_type || '',
  opportunity_date: opportunity.opportunity_date?.slice(0, 10) || '',
  estimated_value: opportunity.estimated_value ?? null,
  client_id: opportunity.client?.id ?? null,
  client_name: getOpportunityClientName(opportunity.client),
  zip_code: opportunity.zip_code || '',
  city_id: opportunity.city_id ?? null,
  city_name: formatOpportunityCity(opportunity.city),
  address: opportunity.address || '',
  number: opportunity.number || '',
  complement: opportunity.complement || '',
  district: opportunity.district || '',
  observation: opportunity.observation || '',
});

export const mapFormDataToOpportunityPayload = (
  formData: OpportunityFormData,
  current?: Pick<Opportunity, 'flow_status' | 'proposal_value'>,
): OpportunityPayload => ({
  responsible_id: formData.responsible_id as number,
  client_id: formData.client_id,
  origin_id: formData.origin_id as number,
  flow_status_id: current?.flow_status?.id ?? null,
  description: formData.description.trim(),
  service_type: formData.service_type.trim(),
  estimated_value: formData.estimated_value,
  proposal_value: current?.proposal_value ?? null,
  opportunity_date: formData.opportunity_date,
  observation: toNullableText(formData.observation),
  zip_code: toNullableText(formData.zip_code),
  city_id: formData.city_id,
  address: toNullableText(formData.address),
  number: toNullableText(formData.number),
  complement: toNullableText(formData.complement),
  district: toNullableText(formData.district),
});

// `PUT opportunity/{id}` substitui todos os campos, então alterações parciais
// (ex.: valor da proposta pelo cabeçalho) reenviam a oportunidade inteira.
export const buildOpportunityUpdatePayload = (
  opportunity: Opportunity,
  changes: Partial<OpportunityPayload> = {},
): OpportunityPayload => ({
  ...mapFormDataToOpportunityPayload(
    mapOpportunityToFormData(opportunity),
    opportunity,
  ),
  ...changes,
});
